<template>
	<div v-if="event" v-bind="$attrs">
		<div v-if="!isEditing" class="mb-40">
			<h3 class="fs-22 mb-24">What Kind of Help Do You Need?</h3>
			<div class="px-24 py-32 border flex-col gap-16 md-flex-row md-justify-space lg-gap-52">
				<div style="flex:0 1 240px;">
					<h2 class="fs-18 lh-20 m0">Job Role</h2>
					<p class="mt-8 fs-14 lh-22">Select the role you are looking to fill for your job offer.</p>
				</div>

				<div class="flex wrap" style="gap:10px;">
					<div v-for="{ role, value } of jobRoleChoices" :class="{ 'disabled': isEditing }" class="role-checkbox checkbox-wrap" :key="`role${role.id}`">
						<input :id="`role${role.id}`" v-model="event.jobs" name="event-role" type="radio" :value="value" required/>
						<label :for="`role${role.id}`">
							<div class="box">
								<icon-check-slim />
							</div>
							{{ role.name }}
						</label>
					</div>
				</div>
			</div>
		</div>

		<h3 v-if="!isEditing" class="fs-22 mb-24">Job Information</h3>
		<div :class="{'pad-24 border': !isEditing}" class="grid gap-32">
			<div v-if="!isEditing">
				<div class="flex-split wrap gap-x-16 mb-8">
					<label class="color-violet fw-700" for="event-name">Job Name*</label>
					<small class="fs-12 lh-14">Give your job a descriptive name to help it stand out</small>
				</div>
				<TextField v-model="event.name" placeholder="Stephenson Wedding" required></TextField>
			</div>


			<div class="job-details-date-row">
				<!-- Date -->
				<div class="job-details-date-field">
					<DateField v-model="date" :min="$isoDate(today)" required @change="updateEventTime" :readonly="isEditing">
						<template #label><span class="color-violet fs-16 fw-700">Date*</span></template>
						<template #modal-title>Event Date</template>
					</DateField>
				</div>

				<div class="border-left show-xxl"></div>

				<!-- Time -->
				<div class="job-details-time-field">
					<FieldGroup class="time-fields">
						<template #label>
							<span class="color-violet fs-16 fw-700">Time</span>
						</template>
						<template #default>
							<TimeField v-model="startTime" :required="!!endTime" @change="updateEventTime" />
							<span>to</span>
							<TimeField v-model="endTime" :min="minEndTime" @change="updateEventTime" />
						</template>
					</FieldGroup>
				</div>

				<div class="border-left show-xxl"></div>

				<!-- Hours -->
				<div class="job-details-hours-field">
					<NumberField v-model="minimumPaidHours" class="minimum-paid-hours-field" :min="1" :max="24" required>
						<span class="color-violet fs-16 fw-700">Minimum Paid Hours*</span>
						<template #unit>
							<svg class="block" @mouseenter="showMinimumPaidHoursTooltip = true" @mouseleave="showMinimumPaidHoursTooltip = false" xmlns="http://www.w3.org/2000/svg" width="20" height="21" fill="none" viewBox="0 0 20 21"><path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M10 18C14.1421 18 17.5 14.6421 17.5 10.5C17.5 6.35786 14.1421 3 10 3C5.85786 3 2.5 6.35786 2.5 10.5C2.5 14.6421 5.85786 18 10 18Z"/><path fill="#140047" d="M9.375 9.875H10V14.25H10.625"/><path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.375 9.875H10V14.25H10.625"/><path fill="#140047" d="M10 8C10.5178 8 10.9375 7.58027 10.9375 7.0625C10.9375 6.54473 10.5178 6.125 10 6.125C9.48223 6.125 9.0625 6.54473 9.0625 7.0625C9.0625 7.58027 9.48223 8 10 8Z"/></svg>
							<Tooltip v-model="showMinimumPaidHoursTooltip">
								<p>Enter the minimum amount of hours guaranteed for this job.</p>
							</Tooltip>
						</template>
					</NumberField>	
				</div>
			</div>

			<div>
				<LocationAutocompleteField v-for="index of event.locations.map((location, i) => i)" :key="index" v-model="event.locations[index]" :aria-label="index > 0 ? `Additional Location ${index}` : null" :required="index === 0 && !event.locations.slice(1).some((loc) => !!loc.address)" @input="updateEventTime" @geocoded="onLocationGeocoded">
					<template #label v-if="index === 0"><span class="fw-700 fs-16 color-violet">Location*</span></template>
					<template #augmentation-right>
						<IconButton icon="trash-gray" aria-label="Remove Location" @click="removeLocation(index)" />
					</template>
				</LocationAutocompleteField>
				<button type="button" class="reset gap-4 color-blue mt-12" @click="addLocation">
					<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="none" viewBox="0 0 11 11"><g stroke="#004ADB" stroke-linecap="round" stroke-width="1.5" clip-path="url(#clip0_1_5)"><path d="M9.4853 5.2246 1 5.2246M5.2246 9.4853 5.2246 1"/></g><defs><clipPath id="clip0_1_5"><rect width="11" height="11" fill="#fff" transform="matrix(0 -1 1 0 0 11)"/></clipPath></defs></svg>
					<span class="fs-12 lh-14 fw-500">Add Additional Location</span>
				</button>
			</div>
			

			<div>
				<label for="event-type" class="block mb-12 color-violet fw-700">Job Type*</label>
				<SelectField id="event-type" v-model="jobTypeName">
					<template #options>
						<option v-for="jobType in jobTypes" :value="jobType.name" :key="jobType.id" @change="updateJobType">{{ jobType.name }}</option>
					</template>
				</SelectField>
			</div>

			<div>
				<div class="mb-8 flex-split wrap">
					<div class="flex gap-16">
						<label for="event-notes" class="color-violet fw-700">Notes</label>
						<small class="fs-12">Optional</small>
					</div>
					<small class="fs-12 lh-14">Any additional notes about this job</small>
				</div>
				<textarea id="event-notes" v-model="event.notes"></textarea>
			</div>
		</div>
	</div>
</template>

<script>
import JobEvent from '@/models/JobEvent';
import JobEventLocation from '@/models/JobEventLocation';
import Role from '@/models/Role';
import { DateTime } from 'luxon';
import { fetchJobTypes } from '@/services/swm/api';
import { millisecondsPer } from '@/plugins/time';

export default {
	name: 'EventFormFields',
	inheritAttrs: false,
	model: { prop: 'event' },
	props: {
		event: {
			type: JobEvent,
			required: true,
		},
		isEditing: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			date: null,
			endTime: null,
			showMinimumPaidHoursTooltip: false,
			startTime: null,
			today: DateTime.now().startOf('day').toMillis(),
			todayInterval: null,
			jobTypes: [],
			jobTypeName: '',
		};
	},
	computed: {
		jobRoleChoices() {
			const choices = [];
			const roles = this.$store.getters['roles/list'];

			if (roles) {
				let i = 0;
				let positionId;
				const currentJobs = this.event?.jobs;
				const job = currentJobs && currentJobs[0];
				const studioId = Role.studio.id;

				for (const role of roles) {
				if ((positionId = role.id) !== studioId) {
					choices[i++] = {
					role,
					value: job && job.positionId === positionId ? currentJobs : [{ positionId }],
					};
				}
				}
			}
			return Object.freeze(choices);
		},
		endTimeMs() {
			return this.$toTime(this.endTime, { zone: this.eventTimeZone });
		},
		eventTimeZone() {
			return this.event?.timeZone || this.$localTimeZone;
		},
		isNewEvent() {
			const event = this.event;
			return !!event && !event.id;
		},
		minEndTime() {
			const { startTimeMs } = this;
			return startTimeMs ? this.$toTimeInputString(startTimeMs + millisecondsPer.minute) : null;
		},
		minimumPaidHours: {
			get() {
				return this.event?.minimumPaidHours ?? null;
			},
			set(value) {
				const { event } = this;
				if (event) {
					event.minimumPaidHours = value;
				}
			},
		},
		startTimeMs() {
			return this.$toTime(this.startTime, { zone: this.eventTimeZone });
		},
	},
	methods: {
		updateJobType() {
			this.event.type = this.jobTypes.filter((obj) => obj.name == this.jobTypeName)[0];
		},
		addLocation() {
			const locations = this.event?.locations;
      if (!locations || this.focusFirstEmptyLocationField()) {
        return;
      }
      locations.push(new JobEventLocation());
      setTimeout(this.focusFirstEmptyLocationField.bind(this));
    },
    focusFirstEmptyLocationField() {
      const el = this.$el;
      if (!el) {
        return false;
      }
      const inputs = Array.from(el.querySelectorAll('.location-autocomplete-field .field-input'));
      for (const input of inputs) {
        if (!input.value) {
          input.focus();
          return true;
        }
      }
      return false;
    },
    async onLocationGeocoded(location) {
      await this.$store.dispatch('events/getEventLocationTimeZone', {
        location,
        timestamp: this.event?.startedAt,
      });
      this.updateEventTime();
    },
    removeLocation(index) {
      const locations = this.event?.locations;
      if (!locations || (locations.length === 1 && index === 0 && !locations[0].address)) {
        return;
      }
      locations.splice(index, 1);
      if (locations.length <= 0) {
        locations.push(new JobEventLocation());
        setTimeout(this.focusFirstEmptyLocationField.bind(this));
      }
    },
    updateEventTime() {
      const { date, event } = this;
      if (!event || !date) {
        return;
      }

      let { endTime, endTimeMs, startTime, startTimeMs } = this;

      if (!startTime) {
        startTime = '00:00:01';
        startTimeMs = millisecondsPer.second;
      } else if ((startTimeMs = this.startTimeMs) == null) {
        return;
      }

      if (!endTime) {
        endTime = '23:59:59';
        endTimeMs = millisecondsPer.day - millisecondsPer.second;
      } else if (
        !this.startTime ||
        (endTimeMs = this.endTimeMs) == null ||
        endTimeMs < startTimeMs
      ) {
        /**
         * If an end time is provided, then the form is invalid if either
         * - no start time is provided, or
         * - the provided end time isn't a valid time, or
         * - the end time is earlier than the start time.
         */
        return;
      }

      const zone = this.eventTimeZone;
      event.startedAt = DateTime.fromISO(`${date}T${startTime}`, { zone }).toMillis();
      event.endedAt = DateTime.fromISO(`${date}T${endTime}`, { zone }).toMillis();
    },
  },
	watch: {
		event: {
			handler(event) {
				if (!event) return;
				let date = null;
				let startTime = null;
				let endTime = null;
				const { endedAt, locations, startedAt} = event;
				const zone = this.eventTimeZone;

				if ((date = this.$toDateTime(startedAt, {zone} ))) {
					this.date = date.toISODate();
					if (date.hour !== 0 || date.minute !== 0 || date.second !== 1) {
						startTime = this.$toTimeInputString(date);
					}
				} else {
					this.date = null;
				}
				
				if ( (date = this.$toDateTime(endedAt, {zone})) && (date.hour !== 23 || date.minute !== 59 || date.second !== 59)) {
					endTime = this.$toTimeInputString(date);
				}

				if (!locations.length) {
					locations.push(new JobEventLocation());
				}

				this.startTime = startTime;
				this.endTime = endTime;
			},
			immediate: true,
		},
		jobRoleChoices: {
			handler(jobRoleChoices) {
				// TOOD: could not figure out how to validate this field, so setting lead shooter as a default
				if (jobRoleChoices.length && this.event && !this.event.jobs.length) {
					const role =  jobRoleChoices[2].value[0];
					this.event.jobs = [role];
				}
			},
			immediate: true,
		}
	},
	async mounted() {
		this.jobTypes = await fetchJobTypes();
		this.updateJobType();
		this.jobTypeName = 'Wedding';

		if (this.todayInterval == null) {
			this.todayInterval = setInterval(() => {
				this.today = DateTime.now().startOf('day').toMillis();
			}, 5000);
		}
	},
	destroyed() {
		clearInterval(this.todayInterval);
		this.todayInterval = null;
	},
};
</script>
